import React, {Component} from 'react';
import './DrugOrderFormDetailsPage.css';
import Page from '../../components/page/Page';
import Card from '../../components/card/Card';
import Label from '../../components/label/Label';
import Button from '../../components/button/Button';
import mainAPI from '../../api/MainAPI';
import CardHeader from '../../components/cardHeader/CardHeader';
import LabelAndValue from '../../components/labelAndValue/LabelAndValue';
import utils from '../../utils/utils';
import UpdateTitle from '../../components/updateTitle/UpdateTitle';
import Image from '../../components/image/Image';
import ImagePopup from '../../popups/imagePopup/ImagePopup';
import UploadDrugOrderFormImagePopup from '../../popups/uploadDrugOrderFormImagePopup/UploadDrugOrderFormImagePopup';
import toasts from '../../utils/toasts';
import DrugsPaginatedCard from '../../cards/drugPaginatedCard/DrugsPaginatedCard';
import DrugOrderFormSummaryCard from './DrugOrderFormSummaryCard';
import debounce from 'lodash.debounce';

export default class DrugOrderFormDetailsPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			form: {},
			images: [],
			addMoreMode: false
		};
		this.pendingUpdates = new Set();
		this.drugsCardRef = React.createRef();
		this.summaryCardRef = React.createRef();
		this.debouncedRefreshDrugList = debounce(this._refreshDrugList.bind(this), 10);
		this.debouncedRefreshSummary = debounce(this._refreshSummary.bind(this), 10);
	}

	componentDidMount = () => {
		this._getFormData();
		this._getImages();
	};

	_getFormData = async () => {
		const {match: {params: {id} = {}} = {}} = this.props;
		if (!id) return;

		const form = await mainAPI.drug_order_form.get(id);
		if (form) {
			this.setState({form});
		}
	};

	_getImages = async () => {
		const {match: {params: {id} = {}} = {}} = this.props;
		if (!id) return;

		const images = await mainAPI.drug_order_form_image.for_drug_order_form(id);
		if (images) {
			this.setState({images: Array.isArray(images) ? images : []});
		}
	};

	_showUploadImagePopup = () => {
		const {match: {params: {id} = {}} = {}} = this.props;
		if (!id) {
			toasts.show('Invalid drug order form ID');
			return;
		}

		window.popupController.show(UploadDrugOrderFormImagePopup, {
			drug_order_form_id: id,
			onComplete: () => {
				this._getImages();
			},
			onClose: () => {
				window.popupController.hide(UploadDrugOrderFormImagePopup);
			}
		});
	};

	_showImagePopup = (imageUrl) => {
		const fullUrl = mainAPI.rootURL() + imageUrl;
		window.popupController.show(ImagePopup, {src: fullUrl, token: localStorage.token});
	};

	_getFilter = () => {
		const {match: {params: {id} = {}} = {}} = this.props;
		const filter = {used_drugs: false};
		if (!this.state.addMoreMode && id) {
			filter.drug_order_form_id = id;
		}
		if (this.state.addMoreMode && id) {
			filter.available_for_drug_order_form_id = id;
		}
		return filter;
	};

	_toggleAddMoreMode = () => {
		this.setState({addMoreMode: !this.state.addMoreMode}, () => {
			if (!this.state.addMoreMode) {
				this.debouncedRefreshSummary();
				if (this.drugsCardRef.current && this.drugsCardRef.current.clearSearch) {
					this.drugsCardRef.current.clearSearch();
				}
			}
		});
	};

	_handleDrugCheckboxChange = async (drug, checked) => {
		const {match: {params: {id} = {}} = {}} = this.props;
		if (!id || !drug || !drug.id) return;

		const updateKey = `${drug.id}`;
		if (this.pendingUpdates.has(updateKey)) {
			return;
		}
		
		this.pendingUpdates.add(updateKey);

		try {
			await mainAPI.drug.update({
				id: drug.id,
				drug_order_form_id: checked ? id : null
			});
			
			this.debouncedRefreshDrugList();
			this.debouncedRefreshSummary();
		} catch (error) {
			toasts.show('Failed to update drug', 5000);
			console.error(error);
		} finally {
			this.pendingUpdates.delete(updateKey);
		}
	};

	_refreshDrugList = async () => {
		if (this.drugsCardRef.current && this.drugsCardRef.current.refresh) {
			await this.drugsCardRef.current.refresh();
		}
	};

	_refreshSummary = async () => {
		if (this.summaryCardRef.current && this.summaryCardRef.current.refresh) {
			await this.summaryCardRef.current.refresh();
		}
	};

	render() {
		const {match: {params: {id} = {}} = {}} = this.props;

		const {
			form: {
				form_number = '-',
				supplier_dea_number = '-',
				type = '-',
				ordered_on,
				received_on,
				created_on,
				created_by_name = '-'
			} = {},
			images = []
		} = this.state;

		return (
			<Page className="g-drug-order-form-details-page">
				<UpdateTitle newTitle={`Drug Order Form ${form_number}`} originalTitle="LogRx"/>
				<Card className="g-drug-order-form-details-page-content-component">
					<div className="g-drug-order-form-details-page-header">
						<CardHeader>Drug Order Form Details</CardHeader>

					</div>
					<div className="g-drug-order-form-details-page-content">
						<div className="g-drug-order-form-details-page-content-left">
							<LabelAndValue label="Form Number" value={form_number}/>
							<LabelAndValue label="Supplier DEA Number" value={supplier_dea_number}/>
							<LabelAndValue label="Type" value={type}/>
						</div>
						<div className="g-drug-order-form-details-page-content-right">
							<LabelAndValue
								label="Ordered On"
								value={ordered_on ? utils.display_actual_datetime(utils.server_to_client_datetime(ordered_on)) : '-'}
							/>
							<LabelAndValue
								label="Received On"
								value={(() => {
									if (!received_on || received_on === 'null' || received_on === 'NULL') return '-';
									try {
										const date = utils.server_to_client_datetime(received_on);
										if (!date || isNaN(date.getTime())) return '-';
										return utils.display_actual_datetime(date);
									} catch (e) {
										return '-';
									}
								})()}
							/>
							<LabelAndValue
								label="Created On"
								value={created_on ? utils.display_actual_datetime(utils.server_to_client_datetime(created_on)) : '-'}
							/>
							<LabelAndValue label="Created By" value={created_by_name}/>
						</div>
					</div>
				</Card>
				<Card className="g-drug-order-form-images-card">
					<div className="g-drug-order-form-images-header">
						<CardHeader>Images</CardHeader>
						<Button
							color="white"
							bg="blue"
							onClick={this._showUploadImagePopup}
						>
							+ Upload Image
						</Button>
					</div>
					<div className="g-drug-order-form-images-grid">
						{images.length === 0 ? (
							<Label gray-medium>No images uploaded yet</Label>
						) : (
							images.map((image) => (
								<div
									key={image.id}
									className="g-drug-order-form-image-item"
									onClick={() => this._showImagePopup(image.image_url)}
								>
									<Image
										src={mainAPI.rootURL() + image.image_url}
										headers={{'x-auth-token': localStorage.token}}
										alt="Drug order form image"
									/>
								</div>
							))
						)}
					</div>
				</Card>
				<DrugOrderFormSummaryCard
					ref={this.summaryCardRef}
					drugOrderFormId={id}
				/>
				<DrugsPaginatedCard
					ref={this.drugsCardRef}
					title="Drugs"
					displayNewDrugButton={false}
					showUnit={true}
					pageSize={250}
					useDrugOrderFormColumns={true}
					filter={this._getFilter()}
					addMoreMode={this.state.addMoreMode}
					onDrugCheckboxChange={this._handleDrugCheckboxChange}
					drugOrderFormId={id}
				>
					<Button
						onClick={this._toggleAddMoreMode}
						className="top-of-card-right-button"
						color={this.state.addMoreMode ? "blue" : "white"}
						bg={this.state.addMoreMode ? "white" : "blue"}
					>
						{this.state.addMoreMode ? 'Done' : '+ Add More'}
					</Button>
				</DrugsPaginatedCard>
			</Page>
		);
	}
}




// WEBPACK FOOTER //
// ./src/pages/drugOrderFormDetailsPage/DrugOrderFormDetailsPage.jsx